import React, { useCallback, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Modal } from 'rizzui';
import { toast } from 'sonner';
import { usePrivilege } from '../../../../../components/priviledge/PriviledgeProvider';
import AvatarCard from '../../../../../components/ui/AvatarCard';
import useFetch from '../../../../../hooks/useFetch';
import useHandleErrors from '../../../../../hooks/useHandleErrors';
import { LeadsService } from '../../../../../services/leads/leadsService';
import { LeadComment } from '../../../../../type/entities/leads-type';
import LeadProfileLayout from '../../LeadProfileLayout';

export default function LeadComments() {

    const { id = '' } = useParams<{ id: string }>();
    const { userCan } = usePrivilege();
    const { handleErrors } = useHandleErrors();
    const service = new LeadsService();
    const [newComment, setNewComment] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [deletingCommentId, setDeletingCommentId] = useState<string | null>(null);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState<string | null>(null);

    const [data, isLoading, error, refetch] = useFetch(useCallback(async () => {
        if (!id || id === '') return [];
        const response = await service.getLeadComments(id);
        return response.getResponseData() as LeadComment[];
    }, [id]));

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!newComment.trim() || !id) return;

        setIsSubmitting(true);
        try {
            const response = (await service.addLeadComment(id, newComment.trim())).getResponseData();
            if (response.success) {
                setNewComment('');
                refetch();
                toast.success(response.message || 'Comentario añadido con éxito');
            } else {
                handleErrors(response);
            }
        } catch (error) {
            console.error('Error adding comment:', error);
            toast.error('Error al añadir el comentario');
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleDeleteConfirm = (commentId: string) => {
        setShowDeleteConfirm(commentId);
    };

    const handleDeleteCancel = () => {
        setShowDeleteConfirm(null);
    };

    const handleDelete = async (commentId: string) => {
        setDeletingCommentId(commentId);
        setShowDeleteConfirm(null);

        try {
            const response = await (await service.deleteLeadComment(commentId)).getResponseData();
            if (response.success) {
                toast.success(response.message || 'Comentario eliminado correctamente');
                refetch();
            } else {
                handleErrors(response);
            }
        } catch (error) {
            console.error('Error deleting comment:', error);
            toast.error('Error al eliminar el comentario');
        } finally {
            setDeletingCommentId(null);
        }
    };

    return (
        <LeadProfileLayout isLoading={isLoading}>
            <div className="pt-6">
                {/* Form */}
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">
                        Añadir Comentario
                    </h3>
                    <form onSubmit={handleSubmit} className="space-y-4">
                        <div>
                            <textarea
                                value={newComment}
                                onChange={(e) => setNewComment(e.target.value)}
                                placeholder="Escribe tu comentario aquí..."
                                rows={3}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 resize-none"
                                disabled={isSubmitting}
                            />
                        </div>
                        <div className="flex justify-end">
                            <button
                                type="submit"
                                disabled={!newComment.trim() || isSubmitting}
                                className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                {isSubmitting ? 'Cargando...' : 'Añadir'}
                            </button>
                        </div>
                    </form>
                </div>

                {/* Comments Section */}
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mt-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">
                        Comentarios del Lead
                    </h3>

                    {data && data.data?.length > 0 ? (
                        <div className="space-y-4">
                            {data.data.map((comment: LeadComment) => (
                                <div key={comment.id} className="flex space-x-4 p-4 bg-gray-50 rounded-lg relative group">
                                    <AvatarCard
                                        src="default-avatar.png"
                                        name={comment.user?.name + ' ' + (comment.user?.lastName || '') || 'Usuario desconocido'}
                                        description={new Date(comment.createdAt?.date).toLocaleDateString('es-ES', {
                                            year: 'numeric',
                                            month: 'long',
                                            day: 'numeric',
                                            hour: '2-digit',
                                            minute: '2-digit'
                                        })}
                                        avatarProps={{ name: comment.user?.name || 'U', size: 'sm' }}
                                        className="flex-shrink-0"
                                    />
                                    <div className="flex-1 min-w-0">
                                        <p className="text-sm text-gray-900 whitespace-pre-wrap">
                                            {comment.comment}
                                        </p>
                                    </div>

                                    {/* Delete Button */}
                                    {userCan('edit_leads', 'leads') && (<div className="flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                                        <button
                                            onClick={() => handleDeleteConfirm(comment.id)}
                                            disabled={deletingCommentId === comment.id}
                                            className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-md transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                                            title="Eliminar comentario"
                                        >
                                            {deletingCommentId === comment.id ? (
                                                <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                                </svg>
                                            ) : (
                                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                                </svg>
                                            )}
                                        </button>
                                    </div>)}
                                </div>
                            ))}
                        </div>
                    ) : (
                        <div className="text-center py-8">
                            <p className="text-gray-500">No hay comentarios disponibles</p>
                        </div>
                    )}
                </div>

                {/* Delete Confirmation Modal */}
                <Modal onClose={handleDeleteCancel} isOpen={!!showDeleteConfirm} size="md">
                    <div className="bg-white rounded-lg p-6 w-full">
                        <div className="flex items-center mb-4">
                            <div className="flex-shrink-0 w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
                                <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                                </svg>
                            </div>
                            <div className="ml-4">
                                <h3 className="text-lg font-medium text-gray-900">
                                    Confirmar eliminación
                                </h3>
                            </div>
                        </div>

                        <div className="mb-6">
                            <p className="text-sm text-gray-500">
                                ¿Estás seguro de que deseas eliminar este comentario? Esta acción no se puede deshacer.
                            </p>
                        </div>

                        <div className="flex justify-end space-x-3">
                            <button
                                onClick={handleDeleteCancel}
                                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                            >
                                Cancelar
                            </button>
                            <button
                                onClick={() => handleDelete(showDeleteConfirm as string)}
                                className="px-4 py-2 text-sm font-medium text-white bg-red-600 border border-transparent rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                            >
                                Eliminar
                            </button>
                        </div>
                    </div>
                </Modal>
            </div>
        </LeadProfileLayout>
    );
}